/**
 * User-configurable appearance.
 *
 * Colours are stored as HSL triplets ("243 75% 59%") rather than hex, because
 * Tailwind resolves them through `hsl(var(--primary))`. Keeping the triplet
 * avoids a hex→HSL conversion on every paint, and lets a single hue drive a
 * gradient by varying only the lightness stop.
 *
 * `foreground` is precomputed per swatch: white fails on the lighter hues
 * (amber, lime), so each entry declares the text colour that passes on it.
 */
export interface Swatch {
  name: string;
  /** Display value for the picker chip. */
  hex: string;
  /** "H S% L%" — assigned to a CSS variable. */
  hsl: string;
  /** Readable text colour on top of this swatch. */
  foreground: string;
}

const WHITE = '0 0% 100%';
// Text on a light swatch. Same lightness as before, so contrast is unchanged;
// only the blue cast goes, to sit with the graphite neutrals.
const INK = '214 20% 11%';

/** Action colour: buttons, links, active nav, focus rings, first chart series. */
export const PRIMARY_SWATCHES: Swatch[] = [
  { name: 'Indigo', hex: '#4F46E5', hsl: '243 75% 59%', foreground: WHITE },
  // Three blues the enterprise world actually runs on, ordered by hue with the
  // rest so the row reads as one gradient rather than an append.
  //
  // Two obvious candidates were tried and rejected: a Fluent-style azure
  // (#0079D6) sits fourteen units from Sky and, more decisively, gives white
  // text 4.46:1 — under AA on the buttons this colour drives. An IBM-style
  // #1064FE is nine units from Blue and would read as a duplicate.
  { name: 'Navy', hex: '#1E3B8A', hsl: '224 64% 33%', foreground: WHITE },
  { name: 'Blue', hex: '#2563EB', hsl: '221 83% 53%', foreground: WHITE },
  { name: 'Cobalt', hex: '#0052CC', hsl: '216 100% 40%', foreground: WHITE },
  { name: 'Steel', hex: '#416A90', hsl: '209 38% 41%', foreground: WHITE },
  { name: 'Sky', hex: '#027DBB', hsl: '200 98% 37%', foreground: WHITE },
  { name: 'Cyan', hex: '#07809E', hsl: '192 91% 32.5%', foreground: WHITE },
  { name: 'Teal', hex: '#0B8379', hsl: '175 84% 28%', foreground: WHITE },
  { name: 'Emerald', hex: '#04865D', hsl: '161 94% 27%', foreground: WHITE },
  { name: 'Violet', hex: '#7C3AED', hsl: '262 83% 58%', foreground: WHITE },
  { name: 'Fuchsia', hex: '#C026D3', hsl: '293 69% 49%', foreground: WHITE },
  { name: 'Rose', hex: '#E11D48', hsl: '347 77% 50%', foreground: WHITE },
  { name: 'Amber', hex: '#D97706', hsl: '32 95% 44%', foreground: INK },
];

/**
 * Supporting colour: the AI card gradient's second stop and the second chart
 * series. Deliberately NOT wired to shadcn's `--accent`, which is the hover
 * surface for menus and would turn every hover vivid.
 */
export const SECONDARY_SWATCHES: Swatch[] = [
  { name: 'Slate', hex: '#475569', hsl: '215 25% 27%', foreground: WHITE },
  { name: 'Violet', hex: '#8655F6', hsl: '258 90% 65%', foreground: WHITE },
  { name: 'Purple', hex: '#9333EA', hsl: '272 82% 56%', foreground: WHITE },
  { name: 'Pink', hex: '#DB2777', hsl: '333 71% 51%', foreground: WHITE },
  { name: 'Orange', hex: '#EA580C', hsl: '21 90% 48%', foreground: INK },
  { name: 'Amber', hex: '#F59E0B', hsl: '38 92% 50%', foreground: INK },
  { name: 'Lime', hex: '#65A30D', hsl: '85 85% 35%', foreground: INK },
  { name: 'Emerald', hex: '#10B981', hsl: '160 84% 39%', foreground: INK },
  { name: 'Cyan', hex: '#06B6D4', hsl: '189 94% 43%', foreground: INK },
  { name: 'Blue', hex: '#3B82F6', hsl: '217 91% 60%', foreground: INK },
];

/** Navigation rail and AI card share this set; "Default" defers to the theme. */
export const SURFACE_SWATCHES: Swatch[] = [
  { name: 'Default', hex: '', hsl: '', foreground: '' },
  ...SECONDARY_SWATCHES,
];

export type SidebarMode = 'light' | 'dark';

export interface Appearance {
  primary: string; // Swatch.name
  secondary: string;
  sidebarMode: SidebarMode;
  sidebarColor: string; // 'Default' or a SECONDARY_SWATCHES name
  aiCardColor: string;
  /** Page surface depth, 0 (flattest) to 100 (deepest). 50 is the default. */
  backgroundTone: number;
  /** How much of the navigation rail is shown. */
  navWidth: NavWidth;
}

/**
 * `compact` is the icon-over-label rail the app has always used, and stays the
 * default. `collapsed` drops the labels; `expanded` sets them beside the icon.
 */
export type NavWidth = 'collapsed' | 'compact' | 'expanded';

export const NAV_WIDTHS: Record<NavWidth, string> = {
  collapsed: '64px',
  compact: '88px',
  expanded: '224px',
};

export const DEFAULT_APPEARANCE: Appearance = {
  primary: 'Indigo',
  secondary: 'Violet',
  sidebarMode: 'light',
  sidebarColor: 'Default',
  aiCardColor: 'Default',
  backgroundTone: 50,
  navWidth: 'compact',
};

export const APPEARANCE_STORAGE_KEY = 'synapse-appearance';

const find = (list: Swatch[], name: string, fallback: Swatch) =>
  list.find((s) => s.name === name) ?? fallback;

/** Replaces the lightness of an "H S% L%" triplet, keeping hue and saturation. */
function atLightness(hsl: string, lightness: number): string {
  const [h, s] = hsl.split(' ');
  return `${h} ${s} ${lightness}%`;
}

/**
 * The same, with the saturation pulled down.
 *
 * A row hover wants a whisper of the accent rather than a wash of it. Keeping
 * the swatch's full saturation at a very high lightness still reads as a
 * coloured band across the table; damping it leaves the hue recognisable and the
 * band quiet, which is the difference between a row responding and a row being
 * highlighted.
 */
function asTint(hsl: string, lightness: number, saturationScale: number): string {
  const [h, s] = hsl.split(' ');
  const sat = Math.round(parseFloat(s) * saturationScale);
  return `${h} ${sat}% ${lightness}%`;
}

/**
 * WCAG relative luminance of an "H S% L%" triplet.
 *
 * Lightness is not brightness. Yellow at L=42 is more than twice as luminous as
 * blue at the same lightness, so a single lightness that clears AA for one hue
 * fails it for another. That is exactly how the first attempt at a dimmed rail
 * label went wrong — it picked one opacity for every swatch and dropped six of
 * the light tints below AA — and it is why the dimming below is specified as a
 * contrast floor instead of a fixed value.
 */
export function luminance(hsl: string): number {
  const [h, s, l] = hsl.split(' ').map(parseFloat);
  const sat = s / 100;
  const lig = l / 100;
  const c = (1 - Math.abs(2 * lig - 1)) * sat;
  const hp = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = lig - c / 2;
  const rgb =
    hp < 1 ? [c, x, 0]
    : hp < 2 ? [x, c, 0]
    : hp < 3 ? [0, c, x]
    : hp < 4 ? [0, x, c]
    : hp < 5 ? [x, 0, c]
    : [c, 0, x];
  const lin = (v: number) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  const [r, g, b] = rgb.map((v) => lin(v + m));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * CIE L* — perceptual lightness, which HSL's L is not.
 *
 * A saturated cyan at L=12 is far more luminous than a near-grey violet at
 * L=18, so a rail lift expressed as "+6 lightness" reads as a lift on some hues
 * and as a dip on others. Everything that has to look like a step above or
 * below a surface is measured here instead.
 */
export function lstar(hsl: string): number {
  const y = luminance(hsl);
  return y > 0.008856 ? 116 * Math.cbrt(y) - 16 : 903.3 * y;
}

/** WCAG contrast ratio between two "H S% L%" triplets. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * A damped tint of `hsl` sitting `delta` perceptual steps from `bg`.
 *
 * Scans the lightness range and keeps whichever value lands nearest the target
 * L*, so every hue produces the same *apparent* step regardless of how luminous
 * it happens to be at a given HSL lightness.
 */
function atLstarStep(
  hsl: string,
  bg: string,
  delta: number,
  saturationScale: number,
  up: boolean
): string {
  const target = lstar(bg) + (up ? delta : -delta);
  let best = asTint(hsl, up ? 100 : 0, saturationScale);
  let bestGap = Infinity;
  for (let l = 0; l <= 100; l += 0.5) {
    const candidate = asTint(hsl, l, saturationScale);
    const gap = Math.abs(lstar(candidate) - target);
    if (gap < bestGap) {
      bestGap = gap;
      best = candidate;
    }
  }
  return best;
}

/**
 * The quietest version of `hsl` that still clears `target` against `bg`.
 *
 * Walks a point at a time from `ideal` — the dimming we would like — back
 * towards `safe`, the undimmed value, and stops at the first lightness that
 * passes. A hue with luminance to spare gets the full dimming; one without gets
 * as much as it can afford and no more. Nothing here can return a value that
 * fails, because `safe` is the colour we started from.
 */
function atLeastContrast(
  hsl: string,
  bg: string,
  target: number,
  ideal: number,
  safe: number
): string {
  const step = ideal < safe ? 1 : -1;
  for (let l = ideal; step > 0 ? l <= safe : l >= safe; l += step) {
    const candidate = atLightness(hsl, l);
    if (contrast(candidate, bg) >= target) return candidate;
  }
  return atLightness(hsl, safe);
}

/**
 * Maps a 0-100 tone onto a lightness, bending the line so that tone 50 lands
 * exactly on `mid`. Both halves are linear but their slopes differ, which is
 * what lets the slider sit centred at the theme's existing value instead of
 * a third of the way along.
 */
function toneToLightness(tone: number, flattest: number, mid: number, deepest: number): number {
  const t = Math.min(100, Math.max(0, tone));
  return t <= 50 ? flattest + ((mid - flattest) * t) / 50 : mid + ((deepest - mid) * (t - 50)) / 50;
}

/**
 * The page surfaces, derived from one lightness.
 *
 * Card, muted and border keep their existing distance from the background
 * rather than being set independently — that spacing is what separates a card
 * from the page, so moving the background alone would flatten the hierarchy at
 * one end of the slider and exaggerate it at the other.
 */
function applySurfaces(root: HTMLElement, tone: number, isDark: boolean): void {
  const set = (name: string, hue: string, lightness: number) =>
    root.style.setProperty(name, `${hue} ${Math.min(100, Math.max(0, lightness)).toFixed(1)}%`);

  if (isDark) {
    const bg = toneToLightness(tone, 6, 11, 20);
    // Graphite with a cool bias, not navy.
    //
    // These were shadcn's defaults — 222 47% for the ground, 217 33% for the
    // card — which is a saturated blue-grey that reads as a theme somebody
    // installed. Dropping the saturation by roughly two thirds keeps the
    // coolness and loses the tint, and the difference between the two is most of
    // what separates a stock dark mode from an instrument.
    set('--background', '214 16%', bg);
    set('--card', '214 14%', bg + 6.5);
    set('--popover', '214 14%', bg + 6.5);
    set('--muted', '214 13%', bg + 6.5);
    // Two weights, because a border does two jobs and they are not the same
    // job. `--border` draws the edge of an object against the page; `--border-
    // subtle` draws the rhythm inside one. A table with sixteen dividers at
    // object-edge weight reads as a stack of boxes rather than a list, and the
    // rules compete with the outline meant to contain them.
    set('--border', '214 11%', bg + 17);
    set('--border-subtle', '214 10%', bg + 9);
    set('--input', '214 11%', bg + 17);
    // The header band sits above the card here — lifting it reads as a header
    // where darkening it would read as a gap. Its rule is heavier than
    // `--border` so the first row of data does not join the header.
    set('--table-header', '214 13%', bg + 10);
    set('--table-header-border', '214 11%', bg + 19);
  } else {
    // Light and dark need different elevation strategies, and this line is where
    // that is decided.
    //
    // In dark, a card sits 6.5 points *lighter* than the ground and genuinely
    // floats — light is elevation. In light the card is already white, so the
    // ladder has nowhere to go: at a 98% ground the gap was two points and
    // clamped, which is why cards read as printed on the page rather than laid
    // on it.
    //
    // So the ground drops instead. 96 gives four points of separation that
    // cannot clamp, and the shadow does the rest. 99 stays at the flat end for
    // anyone who wants the old near-white page.
    // 94 at the midpoint. At 96 the page sat eight luminance points from a white
    // card, which over a large flat area reads as the same colour whatever the
    // shadow is doing at the edges.
    const bg = toneToLightness(tone, 98, 94, 89);
    // The same rebalance as dark. 210 40% is the shadcn light slate and reads
    // faintly blue against a white card; at 18% the ground is still cool but
    // stops competing with the card sitting on it.
    //
    // The card stays pure white on purpose: a tinted ground under an untinted
    // card is what gives a page depth without a shadow doing the work.
    set('--background', '214 18%', bg);
    // Pinned to white rather than tracking the ground.
    //
    // It used to be `bg + 2`, so lowering the ground lowered the card with it
    // and the gap stayed at two points however the slider moved. Paper does not
    // get darker because the desk did: the card is white, the ground is tinted
    // beneath it, and the distance between them is what the slider controls.
    set('--card', '0 0%', 100);
    set('--popover', '0 0%', 100);
    set('--muted', '214 16%', bg - 2);
    // Same split as dark. The edge goes a little stronger so the card reads as
    // placed on the page rather than printed into it — it was seventeen points
    // of luminance against the ground, which the eye reads as almost nothing —
    // and the internal rule goes much lighter so a long table stops ruling
    // itself into boxes.
    set('--border', '214 14%', bg - 9);
    // Minus 5.5, not minus 3.
    //
    // The first attempt at this reasoned that the divider was too heavy, which
    // was wrong: twenty-five points of contrast was never too heavy in itself,
    // it was too heavy *relative to the card edge it sat inside*, which was the
    // same value. With the edge now at thirty-five the same divider is clearly
    // subordinate, and at minus 3 it had gone so light that rows ran together
    // and the eye lost its line tracking a job number across to a figure.
    set('--border-subtle', '214 12%', bg - 5.5);
    set('--input', '214 14%', bg - 9);
    // Below the card here, for the same reason it is above it in dark: the
    // band has to be the direction that reads as recessed against its ground.
    set('--table-header', '214 16%', bg - 2.5);
    set('--table-header-border', '214 14%', bg - 12);
  }
}

/**
 * Writes the appearance onto <html> as CSS variables. Everything downstream
 * reads the variables, so no component needs to know about this module.
 */
/**
 * Every token the navigation rail paints, as a value rather than a side effect.
 *
 * Pure and exported so `check:contrast` can run the real thing across every
 * swatch and both modes. The regression this guards against — a dimmed label
 * that fails AA on some hues and not others — is invisible in the one theme a
 * person happens to be looking at, so it has to be checked against all of them
 * or not at all.
 */
export function railTokens(
  sidebarSwatch: Swatch | null,
  primaryHsl: string,
  sidebarDark: boolean
): Record<string, string> {
  // The undecorated rail sits in the same graphite family as the page — 214 at
  // low saturation. It used to be 222 30%, a navy left over from before the
  // surfaces were rebalanced, and side by side the two read as two products.
  const background = sidebarSwatch
    ? atLightness(sidebarSwatch.hsl, sidebarDark ? 12 : 97)
    : sidebarDark
      ? '214 16% 12%'
      : '0 0% 100%';
  // 25 on the light tinted rails left the resting label nowhere to go: dimming
  // is bounded below by AA against a near-white ground and above by the full
  // label, and those two were five perceptual steps apart. Deepening the full
  // label buys the range back — and costs nothing, since darker text on a light
  // rail only reads better.
  const foreground = sidebarDark
    ? '0 0% 96%'
    : sidebarSwatch
      ? atLightness(sidebarSwatch.hsl, 20)
      : '214 16% 26%';

  // Both lifts are steps away from *this* rail, measured perceptually. As fixed
  // lightnesses they worked on the default white rail and all but vanished on
  // the tinted ones; as HSL offsets they inverted on the luminous hues, where
  // "six points lighter" is darker than the cyan it sits on.
  const lift = (delta: number, saturationScale: number) =>
    atLstarStep(primaryHsl, background, delta, saturationScale, sidebarDark);
  const accent = lift(12, sidebarDark ? 0.4 : 0.55);

  return {
    '--sidebar-background': background,
    '--sidebar-foreground': foreground,
    '--sidebar-border': sidebarSwatch
      ? atLightness(sidebarSwatch.hsl, sidebarDark ? 20 : 88)
      : sidebarDark
        ? '214 14% 20%'
        : '214 32% 91%',

    // Resting items sit below the active one instead of beside it. Ten labels
    // at full strength give the eye nowhere to land — including on the page you
    // are actually on — so everything not current is dimmed to the quietest
    // value that still clears AA against this particular rail.
    '--sidebar-foreground-muted': atLeastContrast(
      foreground,
      background,
      4.5,
      sidebarDark ? 74 : 46,
      sidebarDark ? 96 : 25
    ),

    // The active item is primary-on-primary-tint: both stops come from one hue,
    // so the lightness gap is all that separates them. This used to be a
    // constant — 22, picked because it cleared AA for every hue against the old
    // surface — and the moment the surface moved, four combinations dropped
    // under. A constant tuned against one background is a constant that expires,
    // so it now clears the surface it is actually painted on.
    '--sidebar-primary': atLeastContrast(
      primaryHsl,
      accent,
      4.5,
      sidebarDark ? 78 : 22,
      sidebarDark ? 96 : 8
    ),

    // Two surfaces, not one. Hover and active shared a token, so pointing at an
    // item made it look like the item you were on. Active is the larger lift
    // and keeps more of the swatch; hover is a half-step — enough to acknowledge
    // the pointer, not enough to be mistaken for a state. Both are damped: at
    // full saturation the active item read as a block of colour dropped onto the
    // rail rather than a surface raised out of it.
    '--sidebar-accent': accent,
    '--sidebar-hover': lift(6, sidebarDark ? 0.25 : 0.4),
  };
}

export function applyAppearance(appearance: Appearance, isDark: boolean): void {
  const root = document.documentElement;
  const primary = find(PRIMARY_SWATCHES, appearance.primary, PRIMARY_SWATCHES[0]);
  const secondary = find(SECONDARY_SWATCHES, appearance.secondary, SECONDARY_SWATCHES[1]);

  root.style.setProperty('--primary', primary.hsl);
  root.style.setProperty('--primary-foreground', primary.foreground);
  root.style.setProperty('--ring', primary.hsl);
  root.style.setProperty('--chart-1', primary.hsl);

  applySurfaces(root, appearance.backgroundTone, isDark);

  root.style.setProperty('--secondary-brand', secondary.hsl);
  root.style.setProperty('--secondary-brand-foreground', secondary.foreground);
  root.style.setProperty('--chart-2', secondary.hsl);

  // Navigation rail. Mode sets the base; a colour choice tints it, using a
  // lightness appropriate to that mode so text stays readable either way.
  const sidebarDark = appearance.sidebarMode === 'dark';
  const sidebarSwatch =
    appearance.sidebarColor === 'Default'
      ? null
      : find(SECONDARY_SWATCHES, appearance.sidebarColor, SECONDARY_SWATCHES[0]);

  for (const [name, value] of Object.entries(railTokens(sidebarSwatch, primary.hsl, sidebarDark))) {
    root.style.setProperty(name, value);
  }

  // The row hover, carrying the accent's hue rather than a neutral grey.
  //
  // A grey hover reads as a disabled state; a tinted one reads as something
  // responding to the pointer. Derived from whichever swatch the reader chose,
  // so it stays theirs rather than becoming a second brand colour nobody picked.
  //
  // In dark the value has to sit *above* the card, not below it. The card is at
  // the ground plus 6.5, so 23 lifts the row where 19 dropped it — and a hover
  // that darkens reads as pressed rather than pointed at.
  // Both modes land within a couple of points of the same perceived change —
  // light drops about twelve luminance points from the white card, dark lifts
  // about ten off it — so the hover feels like the same gesture in either.
  root.style.setProperty('--row-hover', asTint(primary.hsl, isDark ? 24 : 96.5, isDark ? 0.25 : 0.55));

  // AI summary card — two stops of one hue so the gradient stays coherent.
  const ai =
    appearance.aiCardColor === 'Default'
      ? null
      : find(SECONDARY_SWATCHES, appearance.aiCardColor, SECONDARY_SWATCHES[1]);
  const aiBase = ai ? ai.hsl : isDark ? '243 40% 22%' : '243 45% 32%';
  root.style.setProperty('--ai-card-from', atLightness(aiBase, isDark ? 22 : 32));
  root.style.setProperty('--ai-card-to', atLightness(aiBase, isDark ? 14 : 20));
}

export function readStoredAppearance(): Appearance {
  try {
    const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (!raw) return DEFAULT_APPEARANCE;
    return { ...DEFAULT_APPEARANCE, ...(JSON.parse(raw) as Partial<Appearance>) };
  } catch {
    return DEFAULT_APPEARANCE;
  }
}
